import React, { Fragment, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import {
  Button, Col, Container, Row, Badge, Card, CardBody
} from 'reactstrap';
import { Check, Trash2, MessageSquare, ExternalLink } from 'react-feather';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';
import { baseURL } from '../../../../Services/api/baseURL';
import Loader from '../../../Loader/Loader';
import { useNavigate } from 'react-router-dom';
import useRbac from '../../../../Hooks/useRbac';

const PendingComments = () => {
  const navigate = useNavigate();
  const { can, canAny } = useRbac();
  const token = JSON.parse(localStorage.getItem("token"));

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchPendingComments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/api/blogs/admin/comments/pending`, {
        headers: {
          Authorization: `${token}`,
        }
      });

      setComments(response.data.data || []);
    } catch (error) {
      console.error('Error fetching pending comments:', error);
      Swal.fire({
        title: "Error",
        text: "Failed to fetch pending comments",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (blogId, commentId) => {
    try {
      await axios.patch(
        `${baseURL}/api/blogs/${blogId}/comments/${commentId}/approve`,
        {},
        {
          headers: {
            Authorization: `${token}`,
          }
        }
      );

      Swal.fire({
        title: "Approved!",
        text: "Comment has been approved.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false
      });

      fetchPendingComments();
    } catch (error) {
      console.error('Error approving comment:', error);
      Swal.fire({
        title: "Error",
        text: "Failed to approve comment",
        icon: "error",
        confirmButtonColor: "#fc2c54",
      });
    }
  };

  const handleDelete = async (blogId, commentId) => {
    const result = await Swal.fire({
      title: "Delete Comment?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it"
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(
          `${baseURL}/api/blogs/${blogId}/comments/${commentId}`,
          {
            headers: {
              Authorization: `${token}`,
            }
          }
        );

        Swal.fire({
          title: "Deleted!",
          text: "Comment has been deleted.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });

        fetchPendingComments();
      } catch (error) {
        console.error('Error deleting comment:', error);
        Swal.fire({
          title: "Error",
          text: "Failed to delete comment",
          icon: "error",
          confirmButtonColor: "#fc2c54",
        });
      }
    }
  };

  const handleApproveAll = async () => {
    const result = await Swal.fire({
      title: "Approve All Comments?",
      text: `This will approve all ${comments.length} pending comments.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, approve all"
    });

    if (result.isConfirmed) {
      try {
        const approvalPromises = comments.map(item =>
          axios.patch(
            `${baseURL}/api/blogs/${item.blogId}/comments/${item.comment._id}/approve`,
            {},
            { headers: { Authorization: `${token}` } }
          )
        );

        await Promise.all(approvalPromises);

        Swal.fire({
          title: "All Approved!",
          text: "All pending comments have been approved.",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });

        fetchPendingComments();
      } catch (error) {
        console.error('Error approving all comments:', error);
        Swal.fire({
          title: "Error",
          text: "Failed to approve some comments",
          icon: "error",
          confirmButtonColor: "#fc2c54",
        });
      }
    }
  };

  const columns = [
    {
      name: 'Blog',
      selector: row => row.blogTitle,
      sortable: true,
      cell: row => (
        <div style={{ padding: '8px 0' }}>
          <div
            style={{
              fontWeight: '600',
              color: '#007bff',
              cursor: 'pointer',
              fontSize: '13px'
            }}
            onClick={() => navigate(`/blog-detail/${row.blogId}`)}
          >
            {row.blogTitle?.length > 40 ? row.blogTitle.substring(0, 40) + '...' : row.blogTitle}
          </div>
          <small className="text-muted">/{row.blogSlug}</small>
        </div>
      ),
      width: '200px'
    },
    {
      name: 'Commenter',
      selector: row => row.comment.name,
      sortable: true,
      cell: row => (
        <div style={{ padding: '8px 0' }}>
          <div style={{ fontWeight: '500', fontSize: '13px' }}>{row.comment.name}</div>
          <small className="text-muted">{row.comment.email}</small>
        </div>
      ),
      width: '180px'
    },
    {
      name: 'Comment',
      selector: row => row.comment.content,
      cell: row => (
        <div style={{
          padding: '8px 0',
          fontSize: '13px',
          color: '#374151',
          maxWidth: '300px'
        }}>
          {row.comment.content.length > 100
            ? row.comment.content.substring(0, 100) + '...'
            : row.comment.content}
        </div>
      ),
      grow: 2
    },
    {
      name: 'Date',
      selector: row => row.comment.createdAt,
      sortable: true,
      cell: row => (
        <span style={{ fontSize: '12px', color: '#6c757d' }}>
          {moment(row.comment.createdAt).format("MMM DD, YYYY")}
          <br />
          {moment(row.comment.createdAt).format("h:mm A")}
        </span>
      ),
      width: '120px'
    },
    {
      name: 'Actions',
      omit: !canAny(['blogs.publish', 'blogs.delete']),
      cell: row => (
        <div className="d-flex gap-1">
          {can('blogs.publish') && <Button
            color="success"
            size="sm"
            onClick={() => handleApprove(row.blogId, row.comment._id)}
            title="Approve"
            style={{ padding: '6px 10px' }}
          >
            <Check size={14} />
          </Button>}
          {can('blogs.delete') && <Button
            color="danger"
            size="sm"
            onClick={() => handleDelete(row.blogId, row.comment._id)}
            title="Delete"
            style={{ padding: '6px 10px' }}
          >
            <Trash2 size={14} />
          </Button>}
          <Button
            color="secondary"
            size="sm"
            onClick={() => navigate(`/blog-detail/${row.blogId}`)}
            title="View Blog"
            style={{ padding: '6px 10px' }}
          >
            <ExternalLink size={14} />
          </Button>
        </div>
      ),
      width: '150px',
      center: true
    }
  ];

  useEffect(() => {
    fetchPendingComments();
  }, []);

  return (
    <Container fluid style={{ paddingTop: '30px' }}>
      <Row>
        <Col sm="12">
          <Card>
            <CardBody>
              <Fragment>
                <Row className='pb-4'>
                  <div className='d-flex justify-content-between align-items-center'>
                    <div>
                      {/* <H4>
                        <MessageSquare size={20} className="me-2" />
                        Pending Comments
                      </H4> */}
                      <h5 className="fw-bolder">Pending Comments</h5>
                      <p className="text-muted mb-0">
                        Review and approve comments before they appear on your blog
                      </p>
                    </div>
                    <div className="d-flex gap-2">
                      <Badge color="warning" style={{ fontSize: '14px', padding: '8px 16px' }}>
                        {comments.length} Pending
                      </Badge>
                      {comments.length > 0 && (
                        <Button
                          color="success"
                          onClick={handleApproveAll}
                          style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                          <Check size={16} />
                          Approve All
                        </Button>
                      )}
                    </div>
                  </div>
                </Row>

                {comments.length === 0 && !loading ? (
                  <div className="text-center py-5">
                    <MessageSquare size={48} color="#dee2e6" className="mb-3" />
                    <h5 className="text-muted">No Pending Comments</h5>
                    <p className="text-muted">All comments have been reviewed!</p>
                  </div>
                ) : (
                  <DataTable
                    data={comments}
                    columns={columns}
                    pagination
                    progressPending={loading}
                    progressComponent={<Loader />}
                    customStyles={{
                      rows: {
                        style: {
                          minHeight: '80px',
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                          },
                        },
                      },
                      headCells: {
                        style: {
                          paddingLeft: '8px',
                          paddingRight: '8px',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#495057',
                        },
                      },
                      cells: {
                        style: {
                          paddingLeft: '8px',
                          paddingRight: '8px',
                        },
                      },
                    }}
                  />
                )}
              </Fragment>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PendingComments;